"use client";

import Link from "next/link";
import { useState } from "react";
import {
  Key,
  Zap,
  Shield,
  BookOpen,
  Terminal,
  Code,
  Copy,
  Check,
} from "lucide-react";
import { PlatformNav } from "@/components/platform-nav";
import { AuthGate } from "@/components/auth-gate";

const API_BASE = "https://api.aiarva.com";

const ENDPOINTS = [
  {
    name: "Search",
    description:
      "AI-powered web search with 13+ models and source citations",
  },
  {
    name: "Conversations",
    description: "Multi-turn chat conversations with model selection",
  },
  {
    name: "Images",
    description:
      "AI image generation (OpenAI, xAI, Stability) and inpainting",
  },
  {
    name: "Models",
    description: "AI model catalog, benchmarks, and routing",
  },
  {
    name: "Vision",
    description: "Image and document understanding with multimodal models",
  },
];

const DAILY_CAPS = [
  { action: "Search", cap: "500 / day" },
  { action: "Messages", cap: "200 / day" },
  { action: "Images", cap: "10 / day" },
  { action: "Vision", cap: "50 / day" },
];

const CODE_EXAMPLES = {
  python: `import requests

API_KEY = "aiarva_your_key_here"
headers = {"Authorization": f"Bearer {API_KEY}"}

# AI Search
response = requests.post(
    "${API_BASE}/api/v1/search",
    headers=headers,
    json={"query": "latest AI research papers", "model": "gpt-4.1"}
)
print(response.json())`,

  typescript: `const API_KEY = "aiarva_your_key_here";

// AI Search
const response = await fetch("${API_BASE}/api/v1/search", {
  method: "POST",
  headers: {
    "Authorization": \`Bearer \${API_KEY}\`,
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    query: "latest AI research papers",
    model: "gpt-4.1",
  }),
});
const data = await response.json();
console.log(data);`,

  curl: `curl -X POST ${API_BASE}/api/v1/search \\
  -H "Authorization: Bearer aiarva_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"query": "latest AI research papers", "model": "gpt-4.1"}'`,
};

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }}
      className="absolute right-3 top-3 rounded-md border border-border bg-background p-1.5 text-muted-foreground hover:text-foreground transition-colors"
    >
      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
    </button>
  );
}

export default function PlatformHome() {
  const [activeTab, setActiveTab] = useState<
    "python" | "typescript" | "curl"
  >("python");

  return (
    <AuthGate>
      <PlatformNav />
      <div className="min-h-screen bg-background">
        {/* Hero */}
        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-5xl px-6 py-20 text-center">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
              AIARVA Developer Platform
            </h1>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
              Build intelligent applications with AI search, multi-model chat,
              image generation, and vision — all through a single API.
            </p>
            <div className="mt-8 flex items-center justify-center gap-4">
              <Link
                href="/keys"
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                <Key className="h-4 w-4" />
                Get API Key
              </Link>
              <Link
                href="/docs"
                className="inline-flex items-center gap-2 rounded-lg border border-border px-5 py-2.5 text-sm font-medium hover:bg-accent transition-colors"
              >
                <BookOpen className="h-4 w-4" />
                API Reference
              </Link>
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-5xl px-6 py-16 space-y-20">
          {/* Quickstart */}
          <section>
            <div className="flex items-center gap-3 mb-6">
              <Terminal className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-semibold">Quickstart</h2>
            </div>

            <div className="rounded-xl border border-border bg-card overflow-hidden">
              <div className="flex border-b border-border">
                {(["python", "typescript", "curl"] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-2.5 text-sm font-medium capitalize transition-colors ${
                      activeTab === tab
                        ? "bg-primary/10 text-primary border-b-2 border-primary"
                        : "text-muted-foreground hover:text-foreground hover:bg-accent"
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
              <div className="relative">
                <CopyButton text={CODE_EXAMPLES[activeTab]} />
                <pre className="p-4 overflow-x-auto text-sm leading-relaxed">
                  <code>{CODE_EXAMPLES[activeTab]}</code>
                </pre>
              </div>
            </div>
          </section>

          {/* Authentication */}
          <section>
            <div className="flex items-center gap-3 mb-6">
              <Shield className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-semibold">Authentication</h2>
            </div>

            <div className="rounded-xl border border-border bg-card p-6 space-y-4">
              <p className="text-muted-foreground">
                All API requests require authentication via an API key. Include
                your key in the Authorization header:
              </p>
              <div className="rounded-lg bg-background border border-border p-3 font-mono text-sm">
                Authorization: Bearer aiarva_your_key_here
              </div>
              <p className="text-sm text-muted-foreground">
                API keys use the{" "}
                <code className="rounded bg-accent px-1.5 py-0.5 text-xs">
                  aiarva_
                </code>{" "}
                prefix. Create and manage keys on the{" "}
                <Link href="/keys" className="text-primary hover:underline">
                  API Keys
                </Link>{" "}
                page.
              </p>
            </div>
          </section>

          {/* Available Endpoints */}
          <section>
            <div className="flex items-center gap-3 mb-6">
              <Key className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-semibold">Available Endpoints</h2>
            </div>

            <p className="text-muted-foreground mb-4">
              API keys provide access to AIARVA&apos;s AI endpoints only.
              Internal endpoints (billing, admin, analytics) require session
              auth and are not accessible via API keys.
            </p>

            <div className="grid gap-3 sm:grid-cols-2">
              {ENDPOINTS.map((ep) => (
                <div
                  key={ep.name}
                  className="rounded-xl border border-border bg-card p-4"
                >
                  <code className="rounded bg-primary/10 px-2 py-0.5 text-sm font-medium text-primary">
                    {ep.name}
                  </code>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {ep.description}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Rate Limits */}
          <section>
            <div className="flex items-center gap-3 mb-6">
              <Zap className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-semibold">Rate Limits</h2>
            </div>

            <div className="rounded-xl border border-border bg-card p-6 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-lg border border-border p-4">
                  <h3 className="font-medium">Global Rate Limit</h3>
                  <p className="text-2xl font-bold text-primary mt-1">
                    200 req/min
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Applied per IP address
                  </p>
                </div>
                <div className="rounded-lg border border-border p-4">
                  <h3 className="font-medium">Per-Key Rate Limit</h3>
                  <p className="text-2xl font-bold text-primary mt-1">
                    Configurable
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Set custom RPM per API key
                  </p>
                </div>
              </div>

              <h3 className="font-medium pt-2">Daily Caps per API Key</h3>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {DAILY_CAPS.map(({ action, cap }) => (
                  <div
                    key={action}
                    className="rounded-lg border border-border p-3 text-center"
                  >
                    <p className="text-sm text-muted-foreground">{action}</p>
                    <p className="text-lg font-bold text-primary">{cap}</p>
                  </div>
                ))}
              </div>

              <p className="text-sm text-muted-foreground">
                When rate limited, the API returns{" "}
                <code className="rounded bg-accent px-1.5 py-0.5 text-xs">
                  429 Too Many Requests
                </code>{" "}
                with a{" "}
                <code className="rounded bg-accent px-1.5 py-0.5 text-xs">
                  Retry-After
                </code>{" "}
                header.
              </p>
            </div>
          </section>

          {/* Endpoints table */}
          <section>
            <div className="flex items-center gap-3 mb-6">
              <Code className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-semibold">Core Endpoints</h2>
            </div>

            <div className="rounded-xl border border-border bg-card overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-accent/50">
                    <th className="px-4 py-3 text-left font-medium">Method</th>
                    <th className="px-4 py-3 text-left font-medium">
                      Endpoint
                    </th>
                    <th className="px-4 py-3 text-left font-medium">
                      Description
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {[
                    ["POST", "/api/v1/search", "AI-powered search"],
                    ["POST", "/api/v1/conversations", "Create conversation"],
                    [
                      "POST",
                      "/api/v1/conversations/{id}/messages",
                      "Send message",
                    ],
                    ["POST", "/api/v1/images/generate", "Generate images"],
                    ["GET", "/api/v1/models", "List available models"],
                    [
                      "POST",
                      "/api/v1/vision/analyze",
                      "Analyze image/document",
                    ],
                  ].map(([method, path, desc]) => (
                    <tr
                      key={path}
                      className="hover:bg-accent/30 transition-colors"
                    >
                      <td className="px-4 py-2.5">
                        <span
                          className={`rounded px-2 py-0.5 text-xs font-medium ${
                            method === "POST"
                              ? "bg-green-500/10 text-green-400"
                              : "bg-blue-500/10 text-blue-400"
                          }`}
                        >
                          {method}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 font-mono text-xs">
                        {path}
                      </td>
                      <td className="px-4 py-2.5 text-muted-foreground">
                        {desc}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    </AuthGate>
  );
}
